import React from 'react'
import { BannerTone, IBannerCta, IBannerImage } from '@/types/banners'
import { TranslationType } from '@/types/common'
import { useTranslation } from '@/localization/useTranslation'
import { faClose } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button } from '@/components/Buttons/Button'
import { NextImage } from '@/components/NextImage'
import { isAllowedImageSrc } from '@/utils/images/isAllowedImageSrc'

import classNames from 'classnames'
import classes from './styles.module.scss'

interface IBannerContentProps {
  text: TranslationType
  image?: IBannerImage
  cta?: IBannerCta
  isClickable?: boolean
  dismissible?: boolean
  tone?: BannerTone
  style?: React.CSSProperties
  onClick: (e: React.MouseEvent) => void
  onCtaClick?: (e: React.MouseEvent) => void
  onDismiss: () => void
}

// Shared banner strip: dismiss ✕, text with an optional inline logo flowing
// at the end of the copy, and an optional CTA button (desktop only — on
// mobile the whole banner is the tap target).
export const BannerContent: React.FC<IBannerContentProps> = (props) => {
  const {
    text,
    image,
    cta,
    isClickable = false,
    dismissible = true,
    tone,
    style,
    onClick,
    onCtaClick,
    onDismiss,
  } = props
  const { t } = useTranslation()

  const handleDismissClick = (e: React.MouseEvent) => {
    // The strip itself is clickable; dismissing must not trigger its action
    e.stopPropagation()
    onDismiss()
  }

  const renderInlineLogo = () => {
    if (!image || image.position === 'background') return null
    // Config can override the default 1.4em CSS height (width stays auto)
    const logoStyle = image.height ? { height: image.height } : undefined
    return isAllowedImageSrc(image.src)
      ? <NextImage
        className={classes.logo}
        style={logoStyle}
        src={image.src}
        alt={image.alt}
        // Nominal intrinsic size for the optimizer; layout is controlled
        // by the em-based CSS height (width: auto)
        width={80}
        height={20}
      />
      // eslint-disable-next-line @next/next/no-img-element -- non-allowlisted host: next/image would throw at render, so degrade to an unoptimized <img>
      : <img
        className={classes.logo}
        style={logoStyle}
        src={image.src}
        alt={image.alt}
      />
  }

  return (
    <div
      className={classNames(classes.strip, tone && classes.tone, tone && classes[`tone--${tone}`], {
        [classes['strip--static']]: !isClickable
      })}
      style={style}
      onClick={onClick}
    >
      <div className={classes.text}>
        {dismissible && (
          <FontAwesomeIcon
            onClick={handleDismissClick}
            size='lg'
            icon={faClose}
          />
        )}
        <h3>
          {t(text)}
          {renderInlineLogo()}
        </h3>
      </div>
      {cta?.text && (
        <Button
          className={classes.cta}
          variant={cta.variant ?? 'secondary'}
          style={cta.style}
          onClick={onCtaClick}
        >
          {t(cta.text)}
        </Button>
      )}
    </div>
  )
}
